import {
  AtomicBlockUtils,
  ContentState,
  Editor,
  EditorState,
  RichUtils,
  convertFromRaw,
  convertToRaw,
} from "draft-js";
import "draft-js/dist/Draft.css";
import { useEffect, useRef, useState } from "react";
import {
  BoldIcon,
  ItalicIcon,
  StrikeThroughIcon,
  UnOrderListIcon,
  UnderlineIcon,
} from "themes/Icon";
import ButtonComp from "../Button";
import "./edit_area_comp.scss";

function EditArea({ onChange, value }) {
  const getEditorState = (content) => {
    try {
      if (typeof content === "string") {
        // Check if the string is valid JSON
        const parsedContent = JSON.parse(content);
        if (parsedContent.blocks) {
          // If parsed content has blocks, convert it from raw
          const contentState = convertFromRaw(parsedContent);
          return EditorState.createWithContent(contentState);
        } else {
          // If plain text
          const contentState = ContentState.createFromText(content);
          return EditorState.createWithContent(contentState);
        }
      } else if (typeof content === "object" && content.blocks) {
        // If already parsed object
        const contentState = convertFromRaw(content);
        return EditorState.createWithContent(contentState);
      } else {
        // Empty editor state
        return EditorState.createEmpty();
      }
    } catch (error) {
      return EditorState.createEmpty();
    }
  };

  const [editorState, setEditorState] = useState(getEditorState(value));

  const editor = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editor.current) {
      editor.current.focus();
    }
  }, []);

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
    if (onChange) {
      const contentState = newEditorState.getCurrentContent();
      const rawContent = convertToRaw(contentState);
      onChange(rawContent);
    }
  };

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const toggleInlineStyle = (inlineStyle) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  const toggleBlockType = (blockType) => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const addImage = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        "IMAGE",
        "IMMUTABLE",
        { src: e.target.result }
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = AtomicBlockUtils.insertAtomicBlock(
        editorState,
        entityKey,
        " "
      );
      handleEditorChange(
        EditorState.forceSelection(
          newEditorState,
          newEditorState.getCurrentContent().getSelectionAfter()
        )
      );
    };
    reader.readAsDataURL(file);
  };

  const mediaBlockRenderer = (block) => {
    if (block.getType() === "atomic") {
      return {
        component: Media,
        editable: false,
      };
    }
    return null;
  };

  const Media = (props) => {
    const entity = props.contentState.getEntity(props.block.getEntityAt(0));
    const { src } = entity.getData();

    return (
      <div className="media-container">
        <img
          src={src}
          alt="Editor Media"
          style={{ maxWidth: "200px", maxHeight: "150px", margin: "5px" }}
        />
      </div>
    );
  };

  return (
    <div className="rte-wrapper">
      <label className="rte-label">Message content</label>
      <div className="rte-container">
        <div className="rte-toolbar">
          <ButtonComp
            className="toolbar-btn"
            bgNone
            rounded
            height={20}
            onClick={() => toggleInlineStyle("BOLD")}
          >
            <BoldIcon />
          </ButtonComp>
          <ButtonComp
            className="toolbar-btn"
            bgNone
            rounded
            height={20}
            onClick={() => toggleInlineStyle("ITALIC")}
          >
            <ItalicIcon />
          </ButtonComp>
          <ButtonComp
            className="toolbar-btn"
            bgNone
            rounded
            height={20}
            onClick={() => toggleInlineStyle("UNDERLINE")}
          >
            <UnderlineIcon />
          </ButtonComp>
          <ButtonComp
            className="toolbar-btn"
            bgNone
            rounded
            height={20}
            onClick={() => toggleInlineStyle("STRIKETHROUGH")}
          >
            <StrikeThroughIcon />
          </ButtonComp>
          <div className="list-container">
            <ButtonComp
              className="toolbar-btn"
              bgNone
              rounded
              height={20}
              onClick={() => toggleBlockType("unordered-list-item")}
            >
              <UnOrderListIcon />
            </ButtonComp>
          </div>
        </div>
        <div className="rte-content" onClick={() => editor.current.focus()}>
          <Editor
            editorState={editorState}
            handleKeyCommand={handleKeyCommand}
            onChange={handleEditorChange}
            placeholder="Type the article content..."
            ref={editor}
            blockRendererFn={mediaBlockRenderer}
          />
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".jpg,.png,jpeg"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) {
              addImage(file);
            }
          }}
        />
      </div>
    </div>
  );
}

export default EditArea;
